import { Form, Formik } from 'formik';
import { isEqual } from 'lodash';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';

import TopBar from 'hb-react/frontoffice/apps/Filters/components/TopBar';
import { LOCATION_FIELDS } from 'hb-react/frontoffice/apps/Filters/containers/FiltersApp/constants';
import { handleSetFiltersURL } from 'hb-react/frontoffice/apps/Filters/containers/FiltersApp/handleSetFiltersURL';
import { useResetFilters } from 'hb-react/frontoffice/apps/Filters/containers/FiltersApp/useResetFilters';
import { useTopFilters } from 'hb-react/frontoffice/apps/Filters/containers/FiltersApp/useTopFilters';
import { useUpdateFromMenu } from 'hb-react/frontoffice/apps/Filters/containers/FiltersApp/useUpdateFromMenu';
import { FiltersProvider } from 'hb-react/frontoffice/apps/Filters/contexts/FiltersContext';
import { getCurrentValues } from 'hb-react/frontoffice/apps/Filters/form/getCurrentValues';
import { getInitialValues } from 'hb-react/frontoffice/apps/Filters/form/getInitialValues';
import { getSelectedFiltersCount } from 'hb-react/frontoffice/apps/Filters/form/getSelectedFiltersCount';
import useDisplayNavigationDropShadow from 'hb-react/shared/hooks/theme/useDisplayNavigationDropShadow';
import useSecondaryNavigationTheme from 'hb-react/shared/hooks/theme/useSecondaryNavigationTheme';
import useFromMenuUrlParams from 'hb-react/shared/hooks/useFromMenuUrlParams';
import ThemeProvider from 'hb-react/style/ThemeProvider';

const FiltersApp = forwardRef(
  (
    {
      config,
      fixedControls,
      resetFiltersRef,
      onResetFilters,
      activeExternalFilters,
    },
    ref,
  ) => {
    const displayNavigationDropShadow = useDisplayNavigationDropShadow();
    const secondaryNavigationTheme = useSecondaryNavigationTheme();
    const { search, shouldUpdateFromMenu } = useFromMenuUrlParams({
      excludeKeys: config.ignoredParamsKeys,
    });
    const [searchParams, setSearchParams] = useSearchParams();
    const filtersAppRef = useRef();
    const formikRef = useRef();
    const locationInputRef = useRef();
    const filters = useTopFilters(config);

    const [defaultValues, initialValues] = useMemo(
      () => [
        getInitialValues({
          filters,
          initialFiltersFromConfig: config.initialFilters,
        }),
        getInitialValues({
          filters,
          initialFiltersFromConfig: config.initialFilters,
          searchParamsFilters: Object.fromEntries(searchParams),
        }),
      ],
      [config.initialFilters, filters, searchParams],
    );

    const handleSubmit = (values) => {
      const currentFilters = getCurrentValues({ values });
      const defaultFilters = getCurrentValues({ values: defaultValues });

      const selectedFilters = getSelectedFiltersCount(filters, values);

      config.handleFiltersUpdate(currentFilters, selectedFilters);

      // Update url with values
      handleSetFiltersURL(
        searchParams,
        currentFilters,
        defaultFilters,
        config.ignoredParamsKeys,
      );
    };

    const resetFilters = () => {
      if (locationInputRef.current) {
        locationInputRef.current.handleCleanTextInput();
      }

      if (formikRef.current) {
        formikRef.current.setValues(defaultValues, false);
      }

      if (onResetFilters) {
        onResetFilters();
      }

      setSearchParams({}); // reset URL
      handleSubmit({ query: config.initialFilters });
    };

    const clearLocationFilters = () => {
      // Clear the visual input through the ref
      if (locationInputRef.current) {
        locationInputRef.current.handleCleanTextInput();
      }

      // Clear the Formik form values for location
      if (formikRef.current) {
        const currentValues = formikRef.current.values;
        const newValues = { ...currentValues };

        // Define all location-related fields that should be cleared
        if (newValues.query) {
          LOCATION_FIELDS.forEach((field) => {
            delete newValues.query[field];
          });
        }

        formikRef.current.setValues(newValues, false);

        // Trigger form submission to update URL and events
        handleSubmit(newValues);
      }
    };

    const handleUpdateOnline = (isOnline) => {
      // When online checkbox is checked, disable map search
      if (isOnline && config.handleSetIsSearchingOnMapBounds) {
        config.handleSetIsSearchingOnMapBounds(false);
      }
    };

    useImperativeHandle(ref, () => ({
      appRef: filtersAppRef.current,
      resetFilters: () => {
        resetFilters();
      },
      locationInputRef, // Expose the ref directly
      clearLocationFilters, // Expose the method to clear location filters
    }));

    useResetFilters(resetFiltersRef, resetFilters);

    useUpdateFromMenu(
      shouldUpdateFromMenu,
      handleSubmit,
      initialValues,
      formikRef,
      config,
      filters,
      search,
    );

    // When using the navigation controls the initial state on first render is empty.
    // So we reset the form with the actual initial values to the filters are filled.
    useEffect(() => {
      if (
        formikRef.current &&
        !isEqual(formikRef.current.initialValues, initialValues)
      ) {
        formikRef.current.resetForm({
          values: initialValues,
        });
      }
    }, []);

    if (!filters) {
      return (
        <Box
          ref={filtersAppRef}
          bg={secondaryNavigationTheme.backgroundColor}
          pt={{ _: 5, m: 6 }}
          pb={6}
          minHeight={110}
        />
      );
    }

    return (
      <FiltersProvider
        filters={filters}
        config={config}
        resetFilters={resetFilters}
        locationInputRef={locationInputRef}
        handleUpdateOnline={handleUpdateOnline}
      >
        <Formik initialValues={initialValues} onSubmit={handleSubmit}>
          {(formik) => {
            formikRef.current = formik;

            return (
              <StyledForm
                ref={filtersAppRef}
                tabIndex="-1"
                id="js-filter-app-form"
              >
                <ThemeProvider
                  theme={secondaryNavigationTheme.themeName}
                  fontBrightness={secondaryNavigationTheme.fontBrightness}
                >
                  <Wrapper
                    data-testid="filters-app"
                    $displayNavigationDropShadow={displayNavigationDropShadow}
                    bg={secondaryNavigationTheme.backgroundColor}
                  >
                    <TopBar
                      fixedControls={fixedControls}
                      activeExternalFilters={activeExternalFilters}
                    />
                  </Wrapper>
                </ThemeProvider>
              </StyledForm>
            );
          }}
        </Formik>
      </FiltersProvider>
    );
  },
);

export default FiltersApp;

const StyledForm = styled(Form)`
  position: relative;
`;

const Wrapper = styled(Box).attrs(() => ({
  pt: { _: 5, m: 6 },
  pb: 6,
  minHeight: 110,
  justifyContent: 'space-between',
}))`
  // New app header shadow display only on the bottom;
  ${({ $displayNavigationDropShadow, theme }) =>
    $displayNavigationDropShadow
      ? `box-shadow: 0 6px 6px -6px ${theme.themeVariables.frameDropShadow};`
      : null};
`;
